import CageCard from '@/components/Cages/CageCard';
import CardColumn from '@/components/CardColumn';
import Footer from '@/components/CardColumn/Footer';
import CageCardTemplateWrapper from '@/components/Modals/PrintCageCards/CageCardTemplateWrapper';
import SelectDropDown from '@/components/UI/SelectDropDown';
import ActionList, { ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList';
import { errorToast, successToast } from '@/helpers';
import { _get, _isNil } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { Cage } from '@/model/Cage.model';
import type { ID } from '@/model/Common.model';
import type { State } from '@/model/State.model';
import type { Study } from '@/model/Study.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// @ts-expect-error: riek type is missing
import { RIEInput } from 'riek';
import * as Auth from '@/support/auth';
import { useRequest } from '@/support/Hooks/request';
import Icon from '@/components/UI/Icon';
import Tooltip from '@/components/UI/Tooltip';
import cn from 'classnames';
import { MAX_CAGE_POPULATION } from '@/constants/cage';
import { modalAction } from '@/utils/modal';
import { useApiHook } from '@/support/Hooks/api/useApiHook.ts';
import { ResponseType } from '@/support/ApiService.ts';

interface Card {
  animalId: ID;
  id: ID;
  index: string;
  type: 'card';
}

interface CageCardGroup {
  id: ID;
  title: string;
  duplicateName: boolean;
  items: Array<Animal>;
}

interface CageColumnProps {
  groups: Array<CageCardGroup>;
  group: CageCardGroup;
  cage: Cage;
  cages: Array<Cage>;
  study: Study;
  groupId: ID;
  title: string;
  updateCageName: (cage: Cage, value: string) => void;
  validateCageName: (name: string) => boolean;
  groupIndex: number;
  toggleAddAnimalsModal: (cage: Cage) => void;
  toggleDeleteCageModal: (collection: Cage) => void;
  onCardMove: (dragIndex: any, hoverIndex: any) => void;
  onCardDropped: (dragIndex: any, groupIndex: any) => void;
  animalIdProperty: ID;
  fetchCages: () => void;
  inStudyView: boolean;
}

const CageColumn: FC<CageColumnProps> = ({
  groups,
  group,
  cage,
  cages,
  study,
  groupId,
  title,
  updateCageName,
  validateCageName,
  groupIndex,
  toggleAddAnimalsModal,
  toggleDeleteCageModal,
  onCardMove,
  onCardDropped,
  animalIdProperty,
  fetchCages,
  inStudyView,
}) => {
  const MAX_ANIMALS_PER_CAGE = MAX_CAGE_POPULATION;
  const [printCage, setPrintCage] = useState(false);
  let userCanEdit = true;
  if (study) {
    userCanEdit = Auth.isWriteUserForStudy(study);
  }
  const dispatch = useDispatch();
  const { openModal } = modalAction(dispatch);
  const { atunesIntegrationEnabled } = useSelector((state: State) => ({
    atunesIntegrationEnabled: state?.team?.features?.atunes_colony_management_integration ?? false,
  }));

  const { sendRequest: updateAnimals, requestError } = useRequest({
    route: 'animals.update.bulk',
    method: 'patch',
  });

  const { invoke: splitStudyCage } = useApiHook({
    endpoint: 'POST /api/v1/studies/{studyApiId}/cages/{cageApiId}/split',
    invokeOnInit: false,
    options: { onError: { throw: false, capture: true, toast: false, slug: 'cage-column-split-cage' } },
  });

  const handleSplitCage = async (cage: Cage) => {
    const splitStudyCageResponse = await splitStudyCage({
      path: {
        studyApiId: study.api_id,
        cageApiId: cage.api_id,
      },
    });

    if (splitStudyCageResponse?.type === ResponseType.Success) {
      // Refresh cages data after successful split
      fetchCages();
      successToast('Cage split successfully');
    } else {
      errorToast('Unable to split cage. Please try again or contact support if the issue persists.');
    }
  };

  const buildActionMenuForCage = (cage: Cage) => {
    const deleteDisabled = Boolean(_get(cage, 'subjects.data').length);
    const reorderDisabled = !study || cages.length < 2 || !userCanEdit;
    const splitDisabled = _isNil(study?.api_id) || !userCanEdit || !inStudyView;

    const reorderTooltip = () => {
      if (!userCanEdit) {
        return 'You must have at least write access to the study to reorder cages.';
      }
      if (!study) {
        return 'Cage reordering is only available when viewing cages assigned to a study.';
      }
      if (cages.length < 2) {
        return 'At least 2 cages are required for reordering.';
      }
      return undefined;
    };

    const splitTooltip = () => {
      if (!userCanEdit) {
        return 'You must have at least write access to the study to split cages.';
      }
      if (!inStudyView) {
        return 'Cage splitting is only available when viewing cages within a study.';
      }
      return undefined;
    };

    const actions: ActionProps[] = [
      {
        name: 'Print label',
        action: () => setPrintCage(true),
      },
      {
        name: 'Reorder',
        disabled: reorderDisabled,
        action: () => {
          if (!reorderDisabled) {
            // Pre-process select options for the modal
            const selectOptions = cages.reduce<Array<{ value: string; label: string }>>((acc, c) => {
              if (c.api_id !== cage.api_id) {
                acc.push({
                  value: c.api_id,
                  label: c.name,
                });
              }
              return acc;
            }, []);

            openModal('REORDER_CAGE', {
              cage,
              selectOptions,
              studyApiId: study?.api_id ?? '',
              fetchCages,
            });
          }
        },
        tooltip: reorderTooltip(),
      },
      {
        name: 'Split cage',
        disabled: splitDisabled,
        action: () => handleSplitCage(cage),
        tooltip: splitTooltip(),
      },
    ];

    actions.push(
      {
        name: 'Rename',
        disabled: !userCanEdit,
        action: () => {
          const element = document?.getElementById(`editable_${cage.id}`)?.childNodes?.[0] as HTMLElement;
          element?.click();
        },
      },
      {
        name: 'Delete',
        disabled: !userCanEdit || deleteDisabled,
        action: () => {
          toggleDeleteCageModal(cage);
        },
        className: 'red',
        tooltip: deleteDisabled ? "Cages can't be deleted if animals exist within them" : '',
      }
    );

    return actions;
  };

  const handleCardDrop = async (item: Card) => {
    try {
      const data = {
        animals: [
          {
            id: item.id,
            collection_id: groups[groupIndex].id,
          },
        ],
      };
      await updateAnimals(data);
      onCardDropped(item.index, groupIndex);
    } catch (error) {
      errorToast('Unable to move animal. If this keeps occuring, please contact support');
    }
  };

  const hasSpace = MAX_ANIMALS_PER_CAGE > group.items.length;
  const reqErrorClass = requestError ? 'ba b--red' : '';

  return (
    <>
      <CardColumn
        title={
          <div className="flex">
            {group?.duplicateName ? (
              <div className="flex" style={{ marginTop: '9px' }}>
                <Tooltip render="Duplicate cage name">
                  <Icon icon="critical_alert" className="dark-red" width="22" height="22" />
                </Tooltip>
              </div>
            ) : (
              ''
            )}
            <div
              data-test-component="Cage-rename-input"
              className={cn('flex near-black normal f6', { mt2: group?.duplicateName ?? false })}
              id={`editable_${groupId}`}
            >
              <RIEInput
                value={title}
                change={(value: Record<string, string>) => updateCageName(cage, value.text)}
                propName="text"
                validate={validateCageName}
                classInvalid="ui__error"
                className="ui__editable"
                isDisabled={!userCanEdit}
              />
            </div>
          </div>
        }
        footer={
          userCanEdit && !atunesIntegrationEnabled ? (
            <Footer text="Add animal" onClick={() => toggleAddAnimalsModal(cage)} />
          ) : null
        }
        hasSpace={hasSpace}
        onDrop={handleCardDrop}
        actionMenu={
          <SelectDropDown
            title="Show action menu"
            iconOnly
            className="hover-near-black"
            alignMenu="right"
            portalProps={{ width: 120 }}
          >
            <ActionList actions={cage && buildActionMenuForCage(cage)} />
          </SelectDropDown>
        }
        placeholderCard={
          <CageCard
            subject={
              {
                name: 'placeholder-subject',
                dob: '2000-01-01',
                sex: 'm',
              } as Animal
            }
            studyGroup={
              {
                name: 'placeholder-group',
              } as TreatmentGroup
            }
            inStudyView={inStudyView}
            canDrag={false}
          />
        }
        className={reqErrorClass}
      >
        {group?.items.map((subject, itemIndex) => {
          if (_isNil(subject)) {
            return null;
          }
          const studyGroup =
            study &&
            study.study_groups.find((g) => parseInt(String(g.id)) === parseInt(String(subject.study_group_id)));
          const name = _get(subject, animalIdProperty, subject?.name);
          const index = '' + groupIndex + '-' + itemIndex;
          return (
            <CageCard
              subject={{ ...subject, name }}
              studyGroup={studyGroup}
              key={subject.id}
              id={subject.id}
              index={index}
              inStudyView={inStudyView}
              moveCallback={onCardMove}
              canDrag={userCanEdit}
              dropCallback={async (item) => {
                await handleCardDrop(item);
              }}
            />
          );
        })}
      </CardColumn>
      {printCage && <CageCardTemplateWrapper cageId={cage.id} onComplete={() => setPrintCage(false)} />}
    </>
  );
};

export default CageColumn;
